import React, { useEffect, useState } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { mainMenu, type NavMenu } from '../data/navigation';
import { easeOutStrong } from '../utils/motion';
const dropdownMotion = {
  initial: {
    opacity: 0,
    y: -6
  },
  animate: {
    opacity: 1,
    y: 0
  },
  exit: {
    opacity: 0,
    y: -6
  },
  transition: {
    duration: 0.18,
    ease: easeOutStrong
  }
};
const focusRing = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal';
const topLinkClass = `text-sm font-medium text-ink-muted hover:text-navy transition-colors duration-150 whitespace-nowrap rounded-sm ${focusRing}`;
const mobileLinkClass = 'block px-3 py-2 text-base font-medium text-ink-dark hover:bg-surface-muted rounded-md';
export function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenDropdown(null);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);
  const closeMobile = () => setMobileMenuOpen(false);
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-[padding,background-color,box-shadow] duration-200 ${isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm border-b border-line py-3' : 'bg-white border-b border-line py-4'}`} data-id="element-1004" data-mp="156vhsiyrxa5e">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1005" data-mp="1f6cjkpawn3w3">
        <div className="flex justify-between items-center" data-id="element-1006" data-mp="1k3mjiyvx6xy6">
          <a href="/" className={`flex items-center rounded-sm ${focusRing}`} aria-label="SyNeural home" data-id="element-1007" data-mp="1k21pdadiph0u">
            <img src="https://cdn.magicpatterns.com/uploads/vBy52qowPNFNY53vkUADE7/SyNeural-logo-no-tagline-half-size.svg" alt="SyNeural" className={`w-auto transition-[height] duration-200 ease-out ${isScrolled ? 'h-8' : 'h-10'}`} data-id="element-1008" data-mp="udnw6irfmrat" />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-5 xl:gap-8" aria-label="Main" data-id="element-1009" data-mp="1d5ystuvhtt0h">
            <ul className="flex items-center gap-5 xl:gap-8" data-id="element-1010" data-mp="187nilj1adghq">
              {mainMenu.map(menu => <DesktopMenuItem key={menu.label} menu={menu} isOpen={openDropdown === menu.label} onOpen={() => setOpenDropdown(menu.label)} onClose={() => setOpenDropdown(null)} onToggle={() => setOpenDropdown(openDropdown === menu.label ? null : menu.label)} data-id="element-1011" data-mp="1n57kgh2vyvjo" />)}
            </ul>
            <div className="w-px h-5 bg-line" aria-hidden="true" data-id="element-1012" data-mp="1zfyiq00554dc"></div>
            <a href="/contact" className={`bg-navy hover:bg-navy-light text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors duration-150 shadow-sm whitespace-nowrap ${focusRing}`} data-id="element-1013" data-mp="24dql5nyew8vs">
              Contact Us
            </a>
          </nav>

          <button type="button" className={`lg:hidden text-ink-dark p-2 rounded-md ${focusRing}`} onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu" aria-expanded={mobileMenuOpen} data-id="element-1014" data-mp="1gonvptxjdbqq">
            {mobileMenuOpen ? <X size={24} data-id="element-1015" data-mp="1bp2vzhajkzcy" /> : <Menu size={24} data-id="element-1016" data-mp="1bot7jy55b09d" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav Drawer */}
      <AnimatePresence data-id="element-1017" data-mp="313zmteuwluoa">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.22,
        ease: easeOutStrong
      }} className="lg:hidden bg-white border-b border-line overflow-hidden" data-id="element-1018" data-mp="312g5dkxihdkx">
            <nav className="px-4 pt-2 pb-6 space-y-1 max-h-[80vh] overflow-y-auto" aria-label="Mobile" data-id="element-1019" data-mp="x067lm1l7ni9">
              {mainMenu.map(menu => menu.href ? <a key={menu.label} href={menu.href} className={`${mobileLinkClass} py-3`} onClick={closeMobile} data-id="element-1020" data-mp="s3o04eevhb28">
                    {menu.label}
                  </a> : <div key={menu.label} className="border-b border-line pb-2 mb-2" data-id="element-1021" data-mp="i9p3fuxkijho">
                    <p className="text-xs font-semibold text-ink-muted uppercase tracking-wider px-3 py-2" data-id="element-1022" data-mp="dcowziwt3eq1">{menu.label}</p>
                    {menu.groups?.map((group, groupIndex) => <div key={group.heading ?? groupIndex} className="mb-1" data-id="element-1023" data-mp="4b7mqh62y799">
                        {group.heading && <p className="px-3 pt-2 pb-1 text-xs font-semibold text-teal" data-id="element-1024" data-mp="3vkedjecsaqdy">{group.heading}</p>}
                        {group.items.map(item => <div key={item.href} data-id="element-1025" data-mp="2g170zamx3emc">
                            <a href={item.href} className={mobileLinkClass} onClick={closeMobile} data-id="element-1026" data-mp="2kxxfvumzk466">
                              {item.label}
                              {item.badge && <span className="text-sm font-normal text-ink-muted" data-id="element-1027" data-mp="2oxg0sqet4f90"> · {item.badge}</span>}
                            </a>
                          </div>)}
                      </div>)}
                  </div>)}
              <div className="pt-4 px-3" data-id="element-1028" data-mp="2183ld8svzay9">
                <a href="/contact" onClick={closeMobile} className="block w-full text-center bg-navy text-white font-medium px-5 py-3 rounded-md shadow-sm" data-id="element-1029" data-mp="2616drdlxgecv">
                  Contact Us
                </a>
              </div>
            </nav>
          </motion.div>}
      </AnimatePresence>
    </header>;
}
type DesktopMenuItemProps = {
  menu: NavMenu;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onToggle: () => void;
};
function DesktopMenuItem({
  menu,
  isOpen,
  onOpen,
  onClose,
  onToggle
}: DesktopMenuItemProps) {
  if (menu.href) {
    return <li data-id="element-1030" data-mp="2locfggxl4hb1">
        <a href={menu.href} className={topLinkClass} data-id="element-1031" data-mp="2lolvpcstva3j">
          {menu.label}
        </a>
      </li>;
  }
  const menuId = `menu-${menu.label.toLowerCase().replace(/[^a-z]+/g, '-')}`;
  return <li className="relative" onMouseEnter={onOpen} onMouseLeave={onClose} onBlur={event => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) onClose();
  }} data-id="element-1032" data-mp="1szx17wma230r">
      <button type="button" onClick={onToggle} className={`${topLinkClass} flex items-center gap-1 py-2`} aria-expanded={isOpen} aria-controls={menuId} data-id="element-1033" data-mp="3ic8ngxeyi4an">
        {menu.label}
        <ChevronDown size={14} aria-hidden="true" className={`transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`} data-id="element-1034" data-mp="3lteinu9eskaq" />
      </button>
      <AnimatePresence data-id="element-1035" data-mp="3bwryl7owpx9r">
        {isOpen && <motion.div {...dropdownMotion} id={menuId} className={`absolute top-full pt-2 ${menu.width ?? 'w-72'} ${menu.align === 'right' ? 'right-0' : 'left-0'}`} data-id="element-1036" data-mp="3by3359q6f91x">
            <div className="bg-white rounded-lg shadow-lg border border-line py-2" data-id="element-1037" data-mp="8hmha1syk8y0">
              {menu.groups?.map((group, groupIndex) => <div key={group.heading ?? groupIndex} className={groupIndex > 0 ? 'border-t border-line mt-2 pt-2' : ''} data-id="element-1038" data-mp="3uvw5oaww1gys">
                  {group.heading && <p className="px-4 pt-2 pb-1 text-xs font-semibold text-teal" data-id="element-1039" data-mp="3n07u46fsqgk">{group.heading}</p>}
                  {group.items.map(item => <div key={item.href} data-id="element-1040" data-mp="w5no96ahnhbb">
                      <a href={item.href} onClick={onClose} className="block px-5 py-3 text-sm text-ink-dark hover:bg-surface-muted focus-visible:bg-surface-muted focus-visible:outline-none transition-colors duration-150" data-id="element-1041" data-mp="w6p1nhez8fw7">
                        <span className="font-semibold" data-id="element-1042" data-mp="2jdudspch3ahv">{item.label}</span>
                        {item.badge && <span className="text-ink-muted font-normal" data-id="element-1043" data-mp="2eiotfuypy3vw"> · {item.badge}</span>}
                        <span className="block text-xs text-ink-muted mt-1 leading-relaxed" data-id="element-1044" data-mp="3b6bobog59e78">{item.description}</span>
                      </a>
                    </div>)}
                </div>)}
            </div>
          </motion.div>}
      </AnimatePresence>
    </li>;
}